/**
 * Gobodo Object Type Verifier
 * 
 * Utility for verifying and auto-creating Gobodo object types before operations.
 * Ensures object types exist before creating records, fields, or other dependent objects.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import GobodoConfig, { getSchemaId } from '../config/gobodoConfig';
import { getGobodoObjectTypeByKeyInternal, createGobodoObjectType, getGobodoObjectTypeByKey } from '../services/gobodo/gobodoObjectService';
import type { CustomObject } from '../services/queries/objectQueries';
import { handleGobodoError, logGobodoError } from './gobodoErrorHandler';
import { retryWithBackoff } from './gobodoRetry';
import { logMissingObjectType } from './gobodoObjectTypeDiagnostics';
import { normalizeToGobodoFormat } from './objectApiNameNormalizer';

/** In-memory cache of resolved Gobodo object type numeric IDs (per session). */
const ENSURED_OBJECT_TYPE_IDS = new Map<string, number>();

function rememberObjectTypeId(cacheKey: string, numericId: number): number {
  if (Number.isFinite(numericId)) {
    ENSURED_OBJECT_TYPE_IDS.set(cacheKey, numericId);
  }
  return numericId;
}

/**
 * Ensure an object type exists, creating it if necessary
 * @param apiName - Object type API name (e.g., "PageLayoutTemplate__c")
 * @param objectData - Optional object data for creation if it doesn't exist
 * @returns Promise that resolves with the object type ID (numeric)
 */
export async function ensureObjectTypeExists(
  apiName: string,
  objectData?: Partial<CustomObject>
): Promise<number> {
  const cacheKey = normalizeToGobodoFormat(apiName);
  const cached = ENSURED_OBJECT_TYPE_IDS.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  try {
    // Use Internal (no queue) since ensureObjectTypeExists is only called from
    // createGobodoField/createGobodoRecord/pageLayout which already hold the queue slot
    const existing = await getGobodoObjectTypeByKeyInternal(apiName, false);
    
    // Extract numeric ID from object type ID (e.g., "obj_123" -> 123)
    const numericId = parseInt(existing.id.replace('obj_', ''), 10);
    if (!isNaN(numericId)) {
      return rememberObjectTypeId(cacheKey, numericId);
    }
    
    // If ID format is unexpected, try to get from Gobodo API directly
    const client = getGobodoClient();
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    const gobodoType = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeByKey(apiName, { maxFields: 0 }),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const idNum = Number((gobodoType as { id?: unknown }).id);
    return rememberObjectTypeId(cacheKey, idNum);
  } catch (error: any) {
    // Only treat actual 404s as "not found". Transient 500s should propagate.
    if (!isNotFoundError(error)) {
      // Server/network/auth error — propagate as-is so callers know it's transient
      const gobodoError = handleGobodoError(error, {
        operation: 'ensureObjectTypeExists',
        objectType: apiName
      });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }

    // Log missing object type for diagnostics
    logMissingObjectType(apiName, 'ensureObjectTypeExists', error.message);

    // Pricebook__c must be created by the setup script (with parentid = PRICEBOOK); do not auto-create
    const pricebookCustomKey = normalizeToGobodoFormat('Pricebook');
    if (apiName === pricebookCustomKey) {
      throw new Error(
        `Object type ${apiName} does not exist. Run the setup script first: npm run setup:gobodo-pricebook (creates ${apiName} with parentid = PRICEBOOK and relationship skin fields).`
      );
    }

    // Product__c must be created by the setup script (with parentid = STRIPE_PRODUCT); do not auto-create
    const productCustomKey = normalizeToGobodoFormat('Product');
    if (apiName === productCustomKey) {
      throw new Error(
        `Object type ${apiName} does not exist. Run the setup script first: npm run setup:gobodo-product (creates ${apiName} with parentid = STRIPE_PRODUCT).`
      );
    }

    // Object type doesn't exist - create it if objectData provided
    if (objectData) {
      try {
        const created = await createGobodoObjectType({
          apiName,
          label: objectData.label || apiName.replace(/__c$/, '').replace(/_/g, ' '),
          pluralLabel: objectData.pluralLabel || `${objectData.label || apiName}s`,
          description: objectData.description || `Auto-created object type: ${apiName}`,
          icon: objectData.icon || 'Package',
          category: objectData.category || 'custom',
          isCustom: true,
          isActive: true,
        });
        
        const numericId = parseInt(created.id.replace('obj_', ''), 10);
        if (!isNaN(numericId)) {
          return rememberObjectTypeId(cacheKey, numericId);
        }
        
        // Fallback: get from Gobodo API
        const client = getGobodoClient();
        const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
        const gobodoType = await retryWithBackoff(
          () => objectTypesApi.getObjectTypeByKey(apiName, { maxFields: 0 }),
          { maxRetries: 2, initialDelayMs: 1000 }
        );
        const idNum = Number((gobodoType as { id?: unknown }).id);
        return rememberObjectTypeId(cacheKey, idNum);
      } catch (createError: any) {
        const gobodoError = handleGobodoError(createError, {
          operation: 'ensureObjectTypeExists',
          objectType: apiName
        });
        if (gobodoError) {
          logGobodoError(gobodoError);
          throw gobodoError;
        }
        throw createError;
      }
    }
    
    // Object type genuinely doesn't exist (404) and no creation data provided
    throw new Error(`Object type ${apiName} does not exist and no creation data provided`);
  }
}

/**
 * Check if an object type exists
 * @param apiName - Object type API name
 * @returns Promise that resolves to true if exists, false otherwise
 */
export async function objectTypeExists(apiName: string): Promise<boolean> {
  try {
    await getGobodoObjectTypeByKey(apiName, false);
    return true;
  } catch (error: any) {
    if (isNotFoundError(error)) {
      return false;
    }
    // Re-throw unexpected errors (network, auth, etc.)
    throw error;
  }
}

/**
 * Helper to check if error is a "not found" error
 */
function isNotFoundError(error: any): boolean {
  return error.response?.status === 404 || 
         error.status === 404 ||
         (error instanceof Error && error.message.includes('not found'));
}
